/**
 * useSystemCommonCode Hook
 * 공통코드 관리 훅
 */
import { useState, useEffect, useCallback } from "react";
import { commonCodeApi } from "../api/commonCodeApi";
import { CommonCodeGroup, CommonCodeItem } from "../types/commonCode.types";
import {
    extractCodesFromResponse,
    extractGroupsFromResponse,
    extractGroupsFromCodes,
    sortCodes,
} from "../../shared/lib/commonCodeParserHelpers";

export const useSystemCommonCode = () => {
    const [groups, setGroups] = useState<CommonCodeGroup[]>([]);
    const [groupsLoading, setGroupsLoading] = useState(false);
    const [groupsExpanded, setGroupsExpanded] = useState(false);
    const [expandedGroupCodes, setExpandedGroupCodes] = useState<Set<string>>(new Set());
    const [groupCodesCache, setGroupCodesCache] = useState<Record<string, CommonCodeItem[]>>({});
    const [groupCodesLoading, setGroupCodesLoading] = useState<Record<string, boolean>>({});
    const [allCodesCache, setAllCodesCache] = useState<CommonCodeItem[]>([]);

    useEffect(() => {
        const fetchCommonCode = async () => {
            try {
                const response = await commonCodeApi.getCommonCode();
                const codes = extractCodesFromResponse(response);
                setAllCodesCache(codes);

                let uniqueGroups = extractGroupsFromResponse(response);
                if (uniqueGroups.length === 0) {
                    uniqueGroups = extractGroupsFromCodes(codes);
                }
                setGroups(uniqueGroups);
            } catch {
                // 에러 시 조용히 실패
            }
        };

        fetchCommonCode();
    }, []);

    const fetchGroups = useCallback(async () => {
        if (groupsExpanded && groups.length > 0) {
            setGroupsExpanded(false);
            setExpandedGroupCodes(new Set());
            return;
        }

        if (groups.length > 0) {
            setGroupsExpanded(true);
            setExpandedGroupCodes(new Set());
            return;
        }

        setGroupsLoading(true);
        try {
            const response = await commonCodeApi.getCommonCode();
            let groupsData = extractGroupsFromResponse(response);

            if (groupsData.length === 0) {
                const codes = extractCodesFromResponse(response);
                groupsData = extractGroupsFromCodes(codes);
            }

            setGroups(groupsData);
            setGroupsExpanded(true);
            setExpandedGroupCodes(new Set());
        } catch {
            // 에러 처리
        } finally {
            setGroupsLoading(false);
        }
    }, [groupsExpanded, groups.length]);

    const toggleGroupCodes = useCallback(async (grpCode: string) => {
        const isExpanded = expandedGroupCodes.has(grpCode);

        if (isExpanded) {
            setExpandedGroupCodes(prev => {
                const newSet = new Set(prev);
                newSet.delete(grpCode);
                return newSet;
            });
            return;
        }

        setExpandedGroupCodes(prev => new Set(prev).add(grpCode));

        if (groupCodesCache[grpCode]) return;

        setGroupCodesLoading(prev => ({ ...prev, [grpCode]: true }));

        const filteredCodes = allCodesCache.filter(code => code.grpCode === grpCode);
        const sortedCodes = sortCodes(filteredCodes);

        setGroupCodesCache(prev => ({ ...prev, [grpCode]: sortedCodes }));
        setGroupCodesLoading(prev => ({ ...prev, [grpCode]: false }));
    }, [expandedGroupCodes, groupCodesCache, allCodesCache]);

    return {
        groups,
        groupsLoading,
        groupsExpanded,
        fetchGroups,
        expandedGroupCodes,
        groupCodesCache,
        groupCodesLoading,
        toggleGroupCodes,
    };
};
